import React, { useState } from 'react'
import { Textarea } from '~/lib/ui/textarea'
import { Button } from '~/lib/ui/button'
import StarRating from './StarRating'
import { apiPatch } from '~/util/api'
import { useToast } from '~/lib/ui/toast'
import Modal from '~/components/_layouts/Modal/index'
import { useTranslation } from '~/hooks/useTranslation'
import { useBenefits } from '../../BenefitsProvider'
import YnsIcon from '~/components/icons/YnsIcon'

const CandidateReviewFeedbackForm = ({
  choices,
  admonition,
  serviceRequestId,
  serviceRequestType,
  serviceRequestName,
}) => {
  const [open, setOpen] = useState(false)
  const { setResumeReview, setServiceRequest } = useBenefits()
  const { t } = useTranslation('CandidateReviewFeedback')
  const [rating, setRating] = useState(0)
  const [coachRating, setCoachRating] = useState(0)
  const [feedback, setFeedback] = useState('')
  const [errorVisible, setErrorVisible] = useState(false)
  const [morePrepared, setMorePrepared] = useState('')
  const onSuccess = (response) => {
    setOpen(false)
    if (response?.service_request) {
      toast({
        title: t('toast_title'),
        description: t('toast_description'),
      })
      setResumeReview(response.service_request)
      setServiceRequest(response.service_request)
    }
  }

  const { toast } = useToast()

  const handleSubmit = async () => {
    if (rating === 0 || coachRating === 0) {
      setErrorVisible(true)
      return
    }

    setErrorVisible(false)

    const formData = new FormData()
    formData.append('feedback_service_request[more_prepared]', morePrepared)
    formData.append('feedback_service_request[rating]', rating)
    formData.append('feedback_service_request[coach_rating]', coachRating)
    formData.append('feedback_service_request[feedback]', feedback)
    formData.append(
      'feedback_service_request[service_request_type]',
      serviceRequestType,
    )
    formData.append(
      'feedback_service_request[service_request_id]',
      serviceRequestId,
    )
    formData.append('_method', 'patch') // Tell Rails to treat as PATCH

    try {
      const response = await apiPatch(
        '/coach_services/service_request_rating',
        formData,
      )
      onSuccess?.(response.data) // Close modal from parent
    } catch (error) {
      console.error('Error submitting feedback:', error)
      toast({
        title: t('failed_toast_title'),
        description: t('failed_toast_description'),
        variant: 'destructive',
      })
    }
  }

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} variant="coachcta">
        <YnsIcon name="star" className="mr-2" />
        {t('cta')}
      </Button>
    )
  }

  const showMorePreparedQuestion = [
    'career_pivot_planning',
    'orientation_call',
    'mock_interview',
    'retirement_support',
  ].includes(serviceRequestType)

  const rateYourServiceQuestion = t('rate_your_service', { serviceRequestName })
  const rateYourCoachQuestion = t('rate_your_coach')
  const helpUsImproveStatement = t('help_us_improve')

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={t('dialog_title', { serviceRequestName })}
      variant="secondary"
      size="md"
      actions={[
        <Button
          key="submit"
          variant="coachcta"
          type="button"
          onClick={handleSubmit}
        >
          {t('submit')}
        </Button>,
        <Button key="cancel" variant="ghost" type="button" onClick={onSuccess}>
          {t('no_thanks')}
        </Button>,
      ]}
    >
      <form className="flex flex-col gap-6">
        <div>
          <p className="text-md mb-2 font-medium">{helpUsImproveStatement}</p>
        </div>
        {/* Additional question for certain service types */}
        {showMorePreparedQuestion && (
          <MorePreparedQuestion
            setMorePrepared={setMorePrepared}
            morePrepared={morePrepared}
            serviceRequestType={serviceRequestType}
          />
        )}

        <div>
          <p className="text-md mb-2 font-medium" id="candidate-rating-label">
            {t(`${serviceRequestType}.guidance_rating`, {
              defaultValue: rateYourServiceQuestion,
            })}
          </p>
          <StarRating
            choices={choices}
            value={rating}
            onChange={setRating}
            idPrefix="candidate"
            labelledBy="candidate-rating-label"
          />
        </div>

        <div>
          <p className="text-md mb-2 font-medium" id="coach-rating-label">
            {t(`${serviceRequestType}.coach_rating`, {
              defaultValue: rateYourCoachQuestion,
            })}
          </p>
          <StarRating
            choices={choices}
            value={coachRating}
            onChange={setCoachRating}
            idPrefix="coach"
            labelledBy="coach-rating-label"
          />
        </div>

        {errorVisible && (
          <div
            role="alert"
            className="rounded-md border border-red-500 bg-red-100 px-4 py-2 text-sm text-red-800"
          >
            {t('rate_your_coach_and_service_request', { serviceRequestName })}
          </div>
        )}

        <p className="text-sm text-gray-600">{admonition}</p>

        <div>
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={t('additional_feedback')}
            aria-label={t('feedback')}
          />
        </div>
      </form>
    </Modal>
  )
}

export default CandidateReviewFeedbackForm

const MorePreparedQuestion = ({
  serviceRequestType,
  setMorePrepared,
  morePrepared,
}) => {
  const { t } = useTranslation('CandidateReviewFeedback')
  const feelingMorePreparedQuestion = t('feeling_more_prepared')
  const defaultClasses =
    'px-3 py-2 tracking-tight text-sm w-full focus:outline-none hover:bg-cod-gray-100 hover:rounded text-cod-gray-800 focus:bg-cod-gray-200 focus:text-cod-gray-800 hover:text-foreground'

  return (
    <div>
      <p className="text-md mb-2 font-medium">
        {t(`${serviceRequestType}.more_prepared`, {
          defaultValue: feelingMorePreparedQuestion,
        })}
      </p>
      <div className="flex flex-col gap-3">
        <label
          className={`!flex cursor-pointer items-center gap-2 !font-normal ${defaultClasses}`}
        >
          <input
            type="radio"
            name="morePrepared"
            value="yes"
            checked={morePrepared === t('yes')}
            onChange={() => setMorePrepared(t('yes'))}
            className="!m-0"
          />{' '}
          {t('yes')}
        </label>
        <label
          className={`!flex cursor-pointer items-center gap-2 !font-normal ${defaultClasses}`}
        >
          <input
            type="radio"
            name="morePrepared"
            value="no"
            checked={morePrepared === t('no')}
            onChange={() => setMorePrepared(t('no'))}
            className="!m-0"
          />{' '}
          {t('no')}
        </label>
        <label
          className={`!flex cursor-pointer items-center gap-2 !font-normal ${defaultClasses}`}
        >
          <input
            type="radio"
            name="morePrepared"
            value="not_sure"
            checked={morePrepared === t('not_sure_yet')}
            onChange={() => setMorePrepared(t('not_sure_yet'))}
            className="!m-0"
          />{' '}
          {t('not_sure_yet')}
        </label>
      </div>
    </div>
  )
}
